
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import { getMyOrder } from '../../../services/Clint/UserOrdersApi';

const MyMenuOrders = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [myOrders, setMyOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchMyOrders();
  }, []);

  const fetchMyOrders = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getMyOrder();
      setMyOrders(response?.data?.order || []);
    } catch (err) {
      console.error('خطأ في جلب الطلبات:', err);
      setError(err.message || 'حدث خطأ في جلب الطلبات');
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = (status) => {
    const statusMap = {
      pending: { label: 'قيد الانتظار', color: 'warning' },
      confirmed: { label: 'مؤكد', color: 'info' },
      in_preparation: { label: 'قيد التحضير', color: 'primary' },
      in_prepration: { label: 'قيد التحضير', color: 'primary' },
      ready: { label: 'جاهز', color: 'success' },
      completed: { label: 'مكتمل', color: 'success' },
      finished: { label: 'منتهي', color: 'success' },
      cancelled: { label: 'ملغى', color: 'error' },
    };
    const statusInfo = statusMap[status] || { label: status, color: 'default' };
    return <Chip label={statusInfo.label} color={statusInfo.color} size="small" />;
  };

  const getOrderTypeLabel = (orderType) => {
    if (orderType === 'tables') return 'طاولة';
    if (orderType === 'delivery') return 'ديليفري';
    if (orderType === 'takeaway') return 'تيك اواي';
    return orderType || '-';
  };

  const openOrderDetails = (order) => {
    setSelectedOrder(order || null);
    setDetailsOpen(true);
  };

  const closeOrderDetails = () => {
    setDetailsOpen(false);
    setSelectedOrder(null);
  };

  const formatTime12 = (timeValue) => {
    if (!timeValue) return '-';
    const parsed = new Date(`1970-01-01T${timeValue}`);
    if (Number.isNaN(parsed.getTime())) return timeValue;
    return parsed.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: timeValue.split(':').length >= 3 ? '2-digit' : undefined,
      hour12: true,
    });
  };

  const formatDateTime12 = (dateValue) => {
    if (!dateValue) return '-';
    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) return dateValue;
    return parsed.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getOrderAmount = (order) => {
    const amountValue =
      order?.invoice?.total_amount ??
      order?.total_amount ??
      order?.invoice_total ??
      order?.total_price ??
      order?.total ??
      0;

    const parsed = Number(amountValue);
    return Number.isFinite(parsed) ? parsed.toFixed(2) : String(amountValue || '0');
  };

  const orderDetailsRows = selectedOrder
    ? [
        ['رقم الطلب العام', selectedOrder.general_increment ?? '-'],
        ['نوع الطلب', getOrderTypeLabel(selectedOrder.order_type)],
        ['الحالة', selectedOrder.status || '-'],
        ['رقم الفاتورة', selectedOrder.invoice?.id ?? '-'],
        ['إجمالي الفاتورة', `${getOrderAmount(selectedOrder)} جنية`],
        ['رقم الطاولة', selectedOrder.table_id ?? '-'],
        ['وقت الفتح', formatTime12(selectedOrder.open_time)],
        ['وقت الإغلاق', formatTime12(selectedOrder.close_time)],
        ['التاريخ', selectedOrder.date || '-'],
        ['اين تم الطلب', selectedOrder.type || '-'],
        ['تاريخ الإنشاء', formatDateTime12(selectedOrder.created_at)],
      ]
    : [];

  const selectedOrderItems = Array.isArray(selectedOrder?.order_items)
    ? selectedOrder.order_items
    : [];

  const formatDate = (date) => formatDateTime12(date);

  const getItemTypeLabel = (item) => {
    if (item?.product) return 'منتج';
    if (item?.drinks) return 'مشروب مخصص';
    return '-';
  };

  const sortedOrders = [...myOrders].sort((a, b) => {
    const aPending = a?.status === 'pending' ? 0 : 1;
    const bPending = b?.status === 'pending' ? 0 : 1;

    if (aPending !== bPending) return aPending - bPending;

    const aDate = new Date(a?.created_at || 0).getTime();
    const bDate = new Date(b?.created_at || 0).getTime();
    return bDate - aDate;
  });

  const totalOrdersCount = sortedOrders.length;
  const pendingOrdersCount = sortedOrders.filter((order) => order?.status === 'pending').length;
  const totalOrdersAmount = sortedOrders.reduce(
    (sum, order) => sum + Number(getOrderAmount(order) || 0),
    0
  );

    useEffect(() => {
      document.title = 'My Orders - طلباتي';
         }, []);

  return (
    <Box dir="rtl" sx={{ mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 2.5 },
          mb: 2,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(145deg, rgba(25,118,210,0.10), rgba(46,125,50,0.10))',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <LocalDiningIcon sx={{ fontSize: 34, color: 'primary.main' }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900, fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                طلباتي السابقة من المنيو
              </Typography>
              <Typography variant="body2" color="text.secondary">
                عرض جميع الطلبات مع تفاصيل الفاتورة والعناصر
              </Typography>
            </Box>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip color="primary" label={`عدد الطلبات: ${totalOrdersCount}`} />
            <Chip color="warning" label={`قيد الانتظار: ${pendingOrdersCount}`} />
            <Chip color="success" label={`الإجمالي: ${totalOrdersAmount.toFixed(2)} جنية`} />
          </Stack>
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : myOrders.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#f5f5f5', borderRadius: 3 }}>
          <LocalDiningIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            لا توجد طلبات
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            لم تقم بإنشاء أي طلبات حتى الآن
          </Typography>
        </Paper>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {sortedOrders.map((order, index) => (
            <Paper
              key={index}
              onClick={() => openOrderDetails(order)}
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 2,
                },
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  #{order.general_increment || order.id || order.order_id || 'N/A'}
                </Typography>
                {getStatusChip(order.status)}
              </Stack>

              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Chip label={getOrderTypeLabel(order.order_type)} size="small" variant="outlined" />
                <Chip label={`${getOrderAmount(order)} جنية`} size="small" color="success" />
              </Stack>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {formatDate(order.created_at)}
              </Typography>

              <Button
                fullWidth
                size="small"
                variant="outlined"
                onClick={(event) => {
                  event.stopPropagation();
                  openOrderDetails(order);
                }}
              >
                عرض التفاصيل
              </Button>
            </Paper>
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#f5f7fb' }}>
              <TableRow>
                <TableCell align="right">رقم الطلب</TableCell>
                <TableCell align="right">النوع</TableCell>
                <TableCell align="right">الحالة</TableCell>
                <TableCell align="right">التاريخ</TableCell>
                <TableCell align="right">المبلغ</TableCell>
                <TableCell align="right">الإجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedOrders.map((order, index) => (
                <TableRow
                  key={index}
                  hover
                  onClick={() => openOrderDetails(order)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#f9f9f9' },
                  }}
                >
                  <TableCell align="right">
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      #{order.general_increment || order.id || order.order_id || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={getOrderTypeLabel(order.order_type)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">{getStatusChip(order.status)}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{formatDate(order.created_at)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                      {getOrderAmount(order)} جنية
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(event) => {
                        event.stopPropagation();
                        openOrderDetails(order);
                      }}
                    >
                      عرض التفاصيل
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={detailsOpen}
        onClose={closeOrderDetails}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          تفاصيل الطلب #{selectedOrder?.id || selectedOrder?.order_id || '-'}
        </DialogTitle>

        <DialogContent dividers>
          <Box dir="rtl">
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
              بيانات الطلب الأساسية
            </Typography>

            <Grid container spacing={1.25} sx={{ mb: 2 }}>
              {orderDetailsRows.map(([label, value]) => (
                <Grid item xs={12} md={6} key={label}>
                  <Paper variant="outlined" sx={{ p: 1.25, height: '100%' }}>
                    <Typography variant="caption" color="text.secondary">
                      {label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {String(value ?? '-')}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
              عناصر الطلب ({selectedOrderItems.length})
            </Typography>

            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: '#fafafa' }}>
                  <TableRow>
                    <TableCell align="right">نوع الصنف</TableCell>
                    <TableCell align="right">اسم الصنف</TableCell>
                    <TableCell align="right">الحالة</TableCell>
                    <TableCell align="right">الكمية</TableCell>
                    <TableCell align="right">السعر</TableCell>
                    <TableCell align="right">الإجمالي</TableCell>
                    <TableCell align="right">ملاحظة</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedOrderItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={17} align="center" sx={{ py: 2 }}>
                        لا توجد عناصر داخل هذا الطلب
                      </TableCell>
                    </TableRow>
                  ) : (
                    selectedOrderItems.map((item, idx) => (
                      <TableRow key={item.id || idx}>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            label={getItemTypeLabel(item)}
                            color={item?.product ? 'primary' : item?.drinks ? 'secondary' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {item?.product?.name || item?.drinks?.drink_name || item?.drinks?.name || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{getStatusChip(item.status)}</TableCell>
                        <TableCell align="right">{item.quantity ?? '-'}</TableCell>
                        <TableCell align="right">{item.price ?? '-'}</TableCell>
                        <TableCell align="right">{item.total ?? '-'}</TableCell>
                        <TableCell align="right">{item.note || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeOrderDetails} variant="contained">
            إغلاق
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyMenuOrders;
